import React, { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Mic, Square, Play, Pause, Trash2, Volume2, VolumeX, Loader2 } from 'lucide-react';

// Voice Recorder Component
export const VoiceRecorder = ({ onRecordingComplete, maxDuration = 300, disabled = false }) => {
    const [isRecording, setIsRecording] = useState(false);
    const [recordingTime, setRecordingTime] = useState(0);
    const [audioBlob, setAudioBlob] = useState(null);
    const [audioUrl, setAudioUrl] = useState(null);
    
    const mediaRecorderRef = useRef(null);
    const chunksRef = useRef([]);
    const timerRef = useRef(null);
    const streamRef = useRef(null);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(track => track.stop());
            }
            if (audioUrl) URL.revokeObjectURL(audioUrl);
        };
    }, [audioUrl]);

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;
            
            const mediaRecorder = new MediaRecorder(stream, {
                mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/ogg'
            });
            mediaRecorderRef.current = mediaRecorder;
            chunksRef.current = [];

            mediaRecorder.ondataavailable = (e) => {
                if (e.data.size > 0) {
                    chunksRef.current.push(e.data);
                }
            };

            mediaRecorder.onstop = () => {
                const blob = new Blob(chunksRef.current, { 
                    type: mediaRecorder.mimeType 
                });
                setAudioBlob(blob);
                const url = URL.createObjectURL(blob);
                setAudioUrl(url);
                
                // Stop all tracks
                stream.getTracks().forEach(track => track.stop());
            };

            mediaRecorder.start(1000); // Collect data every second
            setIsRecording(true);
            setRecordingTime(0);

            // Start timer
            timerRef.current = setInterval(() => {
                setRecordingTime(prev => {
                    if (prev >= maxDuration - 1) {
                        stopRecording();
                        return prev;
                    }
                    return prev + 1;
                });
            }, 1000);

        } catch (err) {
            console.error('Error accessing microphone:', err);
            alert('Impossible d\'accéder au microphone. Vérifiez les permissions.');
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        }
    };

    const cancelRecording = () => {
        if (audioUrl) {
            URL.revokeObjectURL(audioUrl);
        }
        setAudioBlob(null);
        setAudioUrl(null);
        setRecordingTime(0);
    };

    const sendRecording = () => {
        if (audioBlob && onRecordingComplete) {
            onRecordingComplete(audioBlob, recordingTime);
            cancelRecording();
        }
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    // If we have a recording, show preview
    if (audioUrl) {
        return (
            <div className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg">
                <audio src={audioUrl} controls className="h-8 flex-1" />
                <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
                    onClick={cancelRecording}
                >
                    <Trash2 className="w-4 h-4" />
                </Button>
                <Button
                    size="sm"
                    className="h-8"
                    onClick={sendRecording}
                >
                    Envoyer
                </Button>
            </div>
        );
    }

    // Recording state
    if (isRecording) {
        return (
            <div className="flex items-center gap-3 p-2 bg-red-50 rounded-lg border border-red-200">
                <div className="flex items-center gap-2">
                    <span className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                    <span className="text-sm font-medium text-red-700">
                        {formatTime(recordingTime)}
                    </span>
                </div>
                <div className="flex-1 h-2 bg-red-200 rounded-full overflow-hidden">
                    <div 
                        className="h-full bg-red-500 transition-all duration-1000"
                        style={{ width: `${(recordingTime / maxDuration) * 100}%` }}
                    />
                </div>
                <Button
                    variant="destructive"
                    size="icon"
                    className="h-8 w-8"
                    onClick={stopRecording}
                >
                    <Square className="w-4 h-4" />
                </Button>
            </div>
        );
    }

    // Default state - mic button
    return (
        <Button
            variant="ghost"
            size="icon"
            className="shrink-0 text-muted-foreground hover:text-primary"
            onClick={startRecording}
            disabled={disabled}
            title="Enregistrer un message vocal"
        >
            <Mic className="w-5 h-5" />
        </Button>
    );
};

// Voice Message Player Component
export const VoiceMessagePlayer = ({ src, duration, isOwn = false }) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [audioDuration, setAudioDuration] = useState(duration || 0);
    const audioRef = useRef(null);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
        const handleDurationChange = () => setAudioDuration(audio.duration || duration || 0);
        const handleEnded = () => {
            setIsPlaying(false);
            setCurrentTime(0);
        };

        audio.addEventListener('timeupdate', handleTimeUpdate);
        audio.addEventListener('durationchange', handleDurationChange);
        audio.addEventListener('ended', handleEnded);

        return () => {
            audio.removeEventListener('timeupdate', handleTimeUpdate);
            audio.removeEventListener('durationchange', handleDurationChange);
            audio.removeEventListener('ended', handleEnded);
        };
    }, [duration]);

    const togglePlay = () => {
        if (audioRef.current) {
            if (isPlaying) {
                audioRef.current.pause();
            } else {
                audioRef.current.play();
            }
            setIsPlaying(!isPlaying);
        }
    };

    const formatTime = (seconds) => {
        if (!seconds || isNaN(seconds)) return '0:00';
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const progress = audioDuration ? (currentTime / audioDuration) * 100 : 0;

    return (
        <div className={`flex items-center gap-2 p-2 rounded-lg min-w-[180px] ${
            isOwn ? 'bg-primary-foreground/10' : 'bg-background/50'
        }`}>
            <audio ref={audioRef} src={src} preload="metadata" />
            
            <Button
                variant="ghost"
                size="icon"
                className={`h-8 w-8 shrink-0 ${isOwn ? 'hover:bg-primary-foreground/20' : ''}`}
                onClick={togglePlay}
            >
                {isPlaying ? (
                    <Pause className="w-4 h-4" />
                ) : (
                    <Play className="w-4 h-4" />
                )}
            </Button>
            
            <div className="flex-1 flex flex-col gap-1">
                <div className="h-1 bg-muted rounded-full overflow-hidden">
                    <div 
                        className={`h-full transition-all ${isOwn ? 'bg-primary-foreground/50' : 'bg-primary'}`}
                        style={{ width: `${progress}%` }}
                    />
                </div>
                <span className="text-[10px] opacity-70">
                    {formatTime(currentTime)} / {formatTime(audioDuration)}
                </span>
            </div>
        </div>
    );
};

// Text-to-Speech Button Component
export const TextToSpeechButton = ({ text, lang = 'fr-FR' }) => {
    const [isSpeaking, setIsSpeaking] = useState(false);

    const speak = () => {
        if (!text) return;

        // Cancel any ongoing speech
        window.speechSynthesis.cancel();

        if (isSpeaking) {
            setIsSpeaking(false);
            return;
        }

        const utterance = new SpeechSynthesisUtterance(text);
        utterance.lang = lang;
        utterance.rate = 1;
        utterance.pitch = 1;

        utterance.onstart = () => setIsSpeaking(true);
        utterance.onend = () => setIsSpeaking(false);
        utterance.onerror = () => setIsSpeaking(false);

        window.speechSynthesis.speak(utterance);
    };

    // Check if speech synthesis is supported
    if (!('speechSynthesis' in window)) {
        return null;
    }

    return (
        <button
            onClick={speak}
            className="opacity-50 hover:opacity-100 transition-opacity p-1"
            title={isSpeaking ? "Arrêter la lecture" : "Écouter le message"}
        >
            {isSpeaking ? (
                <VolumeX className="w-3 h-3" />
            ) : (
                <Volume2 className="w-3 h-3" />
            )}
        </button>
    );
};

export default { VoiceRecorder, VoiceMessagePlayer, TextToSpeechButton };
